import { useState } from "react";
import "../styles/HelpPage.css";

const LogoIcon = () => (
  <svg viewBox="0 0 16 16" fill="none">
    <rect x="1" y="1" width="6" height="6" rx="1.5" fill="white" opacity=".95"/>
    <rect x="9" y="1" width="6" height="6" rx="1.5" fill="white" opacity=".55"/>
    <rect x="1" y="9" width="6" height="6" rx="1.5" fill="white" opacity=".55"/>
    <rect x="9" y="9" width="6" height="6" rx="1.5" fill="white" opacity=".95"/>
  </svg>
);

const TOPICS = [
  { anchor:"signin",  title:"Signing in",         desc:"Google sign-in, OTP login, account switching and session issues.",            icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> },
  { anchor:"account", title:"Account & roles",     desc:"CA vs client access, team members, admin controls and ICAI verification.",    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { anchor:"tally",   title:"Tally import & sync", desc:"How to export XML from Tally, upload data, and re-sync after changes.",       icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> },
  { anchor:"audit",   title:"Audit issues & rules", desc:"Understanding flagged issues, confidence scores, and resolution actions.",   icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35M11 8v3l2 2"/></svg> },
  { anchor:"gst",     title:"GST & compliance",    desc:"GST reconciliation, TDS tracking, filing status and deadline alerts.",        icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2" strokeLinecap="round"><path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"/></svg> },
  { anchor:"billing", title:"Billing & plans",     desc:"Plan limits, upgrading, billing cycle and what's included in each tier.",    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> },
];

const FAQS = {
  signin: [
    { q:"I clicked 'Continue with Google' but nothing happens.", a:"A browser pop-up blocker is likely preventing the sign-in window. Allow pop-ups for this site in your browser settings. In Chrome: click the pop-up icon in the address bar → Always allow." },
    { q:"I'm stuck on 'Redirecting to Google…' indefinitely.", a:"Clear your browser cache and cookies for this site, then reload. If it persists, try an incognito window to resolve stale session state." },
    { q:"I want to sign in with a different Google account.", a:"On the login page, click the 'Switch' link next to your cached account name. This forces the Google account picker." },
    { q:"Mobile OTP isn't arriving.", a:"Check that you entered the correct 10-digit mobile number. OTPs arrive by SMS within 30 seconds. If not, wait 60 seconds and tap 'Resend'." },
    { q:"Can I use both Google and OTP login for the same account?", a:"Yes. Both methods access the same account — your account is tied to your registered email address." },
  ],
  account: [
    { q:"What's the difference between a CA account and a client account?", a:"CA accounts are registered with an ICAI Membership ID and get full access to the audit dashboard. Client accounts are invited by their CA and can only view and respond to audit queries raised for them." },
    { q:"How do I add team members to my CA firm?", a:"Go to Settings → Team. Enter your team member's email and assign a role (Admin or Member). Starter: 2 logins, Audit Pro: 10, Audit Pro 360: unlimited." },
    { q:"My ICAI Membership ID was not recognised during registration.", a:"Double-check your 6-digit membership number without any prefix letters. If the issue persists, contact support with your ICAI certificate." },
  ],
  tally: [
    { q:"How do I export data from Tally for upload?", a:"In Tally ERP9 or TallyPrime: Gateway of Tally → Export → Data. Select XML format, choose your date range, and export. Upload the .xml file on the Register Source page." },
    { q:"How long does a sync take?", a:"Typical Tally XML files sync in 30–90 seconds. Very large files (50,000+ vouchers) may take up to 4 minutes. You will receive an in-app notification when analysis is complete." },
    { q:"Can I re-sync after the client books more entries?", a:"Yes. Go to the source, click Refresh, and upload the updated XML. Audit PrOS creates a new sync run so you can compare current and previous states." },
  ],
  audit: [
    { q:"What does the confidence score on an issue mean?", a:"The score (0–100%) indicates how likely the flagged item is a genuine concern, based on pattern consistency, statistical deviation, and historical CA decisions on similar issues." },
    { q:"How do I resolve an issue and send it to the client?", a:"Open any flagged issue and click 'Raise to client'. This generates a secure query link. The client responds with supporting documents, all tracked inside Audit PrOS." },
    { q:"Are the 100+ audit rules available on the free Starter plan?", a:"Yes. All 100+ adaptive audit rules run on every plan. Detection accuracy is never gated by tier." },
  ],
  gst: [
    { q:"How does GST reconciliation work in Audit PrOS?", a:"Audit PrOS reads your Tally GST ledgers and reconciles them against GSTR-2A, GSTR-2B, and GSTR-3B data. ITC mismatches and discrepancies are flagged automatically in the Compliance section." },
    { q:"Can I file GST returns directly from Audit PrOS?", a:"Direct filing is available on the Audit Pro 360 plan and is currently in beta. On Starter and Audit Pro, Audit PrOS helps you prepare data for filing via the government portal." },
    { q:"How do I see all clients' compliance deadlines in one place?", a:"The Statutory section in the sidebar shows a portfolio-level view of GST and TDS filing statuses — pending, filed, and overdue — across all your synced clients." },
  ],
  billing: [
    { q:"Is the Starter plan really free forever?", a:"Yes. The Starter plan (up to 5 clients, 2 team logins) is free with no time limit and no credit card required." },
    { q:"What happens when I exceed 5 clients on the free plan?", a:"You will be notified and prompted to upgrade to Audit Pro. Existing data for your first 5 clients is always preserved regardless of plan." },
    { q:"How do I upgrade my plan?", a:"Open your profile and go to the Subscription tab. Select the plan that fits your firm size. Audit Pro and Audit Pro 360 pricing is TBD — join the waitlist to be notified first." },
  ],
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hp-faq-item${open ? " open" : ""}`}>
      <button className="hp-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <svg className="hp-faq-chevron" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
      </button>
      <div className="hp-faq-a">{a}</div>
    </div>
  );
}

function ContactModal({ onClose }) {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", category:"", message:"" });
  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 900));
    console.log("[Audit PrOS Help] Support message submitted:", form);
    setLoading(false);
    setSent(true);
  };
  return (
    <div className="hp-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="hp-modal">
        <button className="hp-modal-close" onClick={onClose}>✕</button>
        {sent ? (
          <div className="hp-success-box">
            <div className="hp-success-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1447E6" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg></div>
            <div className="hp-success-title">Message received</div>
            <p className="hp-success-sub">Our team will get back to you within 24 hours.</p>
          </div>
        ) : (
          <>
            <div className="hp-modal-title">Contact support</div>
            <p className="hp-modal-sub">Describe your issue and we'll respond within 24 hours.</p>
            <div className="hp-field"><label>Your name</label><input type="text" placeholder="CA Rajesh Mehta" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))}/></div>
            <div className="hp-field"><label>Email address</label><input type="email" placeholder="ca@yourfirm.com" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))}/></div>
            <div className="hp-field">
              <label>Category</label>
              <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))}>
                <option value="">Select a topic</option>
                <option value="signin">Signing in</option>
                <option value="account">Account &amp; roles</option>
                <option value="tally">Tally import</option>
                <option value="audit">Audit issues</option>
                <option value="gst">GST &amp; compliance</option>
                <option value="billing">Billing &amp; plans</option>
                <option value="other">Something else</option>
              </select>
            </div>
            <div className="hp-field"><label>Describe your issue</label><textarea placeholder="What happened? What did you expect?" value={form.message} onChange={e => setForm(f=>({...f,message:e.target.value}))}/></div>
            <button className="hp-submit" onClick={handleSubmit} disabled={loading||!form.name||!form.email||!form.message}>
              {loading ? <span className="hp-spinner"/> : null}
              {loading ? "Sending…" : "Send message"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function HelpPage({ onBack }) {
  const [active, setActive] = useState("signin");
  const [search, setSearch] = useState("");
  const [showContact, setShowContact] = useState(false);

  const allFaqs = Object.entries(FAQS).flatMap(([cat, items]) => items.map(i => ({...i, cat})));
  const filtered = search.trim().length > 1
    ? allFaqs.filter(f => f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()))
    : null;
  const activeFaqs = filtered || FAQS[active] || [];

  return (
    <>
      <div className="hp-root">
        <nav className="hp-nav">
          <div className="hp-nav-logo" onClick={onBack}>
            <div className="hp-nav-logo-mark"><LogoIcon/></div>
            <span className="hp-nav-brand">
              Audit <span style={{background:"#1447E6",color:"white",padding:"1px 7px",borderRadius:5,fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:16}}>PrOS</span>
            </span>
          </div>
          <button className="hp-back-btn" onClick={onBack}>← Back to sign in</button>
        </nav>

        <div className="hp-body">
          <div className="hp-header">
            <div className="hp-eyebrow"><span className="hp-eyebrow-dot"/>Help Centre</div>
            <h1 className="hp-h1">How can we <em>help?</em></h1>
            <p className="hp-sub">Find answers about signing in, Tally imports, GST compliance, and your Audit PrOS plan.</p>
          </div>

          <div className="hp-search-wrap">
            <span className="hp-search-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg></span>
            <input className="hp-search" type="text" placeholder="Search help articles…" value={search} onChange={e => setSearch(e.target.value)}/>
          </div>

          {!filtered && (
            <div className="hp-topics">
              {TOPICS.map(t => (
                <div key={t.anchor} className={`hp-topic-card${active===t.anchor?" active":""}`} onClick={() => setActive(t.anchor)}>
                  <div className="hp-topic-icon">{t.icon}</div>
                  <div className="hp-topic-title">{t.title}</div>
                  <div className="hp-topic-desc">{t.desc}</div>
                </div>
              ))}
            </div>
          )}

          <div className="hp-section-label">
            {filtered ? `${filtered.length} result${filtered.length!==1?"s":""} for "${search}"` : TOPICS.find(t=>t.anchor===active)?.title}
          </div>
          <div className="hp-faq-list">
            {activeFaqs.length === 0
              ? <p style={{fontSize:14,color:"var(--soft)",padding:"16px 0"}}>No results found. Try different keywords or contact support below.</p>
              : activeFaqs.map((item,i) => <FaqItem key={i} q={item.q} a={item.a}/>)
            }
          </div>

          <div className="hp-contact-card">
            <div>
              <div className="hp-contact-eyebrow">Still stuck?</div>
              <div className="hp-contact-title">Talk to our support team</div>
              <p className="hp-contact-desc">CA-trained specialists respond within 24 hours — usually much faster Mon–Sat, 9 AM–7 PM IST.</p>
            </div>
            <div className="hp-contact-actions">
              <button className="hp-contact-btn hp-contact-btn-white" onClick={() => setShowContact(true)}>Send a message</button>
              <a className="hp-contact-btn hp-contact-btn-ghost" href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</a>
            </div>
          </div>
        </div>

        <footer className="hp-footer">
          <span className="hp-footer-brand">Audit PrOS</span>
          <span className="hp-footer-mid">© 2025 Audit PrOS Technologies Pvt. Ltd.</span>
          <div className="hp-footer-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a onClick={e=>{e.preventDefault();onBack();}}>Sign in</a>
          </div>
        </footer>

        {showContact && <ContactModal onClose={() => setShowContact(false)}/>}
      </div>
    </>
  );
}
